/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { type ComponentType, Suspense, useMemo } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { graphql, loadQuery, type PreloadedQuery, usePreloadedQuery } from 'react-relay';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import { cssMap, jsx } from '@atlaskit/css';
import { type MediaProvider, ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import type { EmojiProvider } from '@atlaskit/emoji/resource';
import { MediaClientProvider } from '@atlaskit/media-client-react/media-client-provider';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import type { RendererAppearance } from '@atlaskit/renderer';
import Spinner from '@atlaskit/spinner/spinner';
import { ContextualAnalyticsData, FireScreenAnalytics, SCREEN } from '@atlassian/analytics-bridge';
import { CommonHubExtensionsDependenciesProvider } from '@atlassian/common-hub-extensions-dependencies-provider';
import {
	GenericErrorPage,
	getMediaProvider,
	helpCenterTypeToProduct,
	type HubAppProps,
	HubBootstrapWrapper,
	HubContext,
	HubErrorBoundary,
	type NavigationStyleContextType,
	NavigationStyleContext,
	type ProductDependenciesProviderProps,
	useHubContext,
} from '@atlassian/company-hub-common';
import { HubSpacingScope } from '@atlassian/company-hub-common/hub-space-density-scope';
import { mapApiToNavigationOptions } from '@atlassian/company-hub-common/navigation-bar-branding';
import { useIsAdvancedThemingEnabled } from '@atlassian/company-hub-common/use-is-advanced-theming-enabled';
import { useIsHubCornerRadiusEnabled } from '@atlassian/company-hub-common/use-is-hub-corner-radius-enabled';
import { deriveRovoWidgetConfig } from '@atlassian/company-hub-common/utils/rovo-config';
import {
	AiWidgetProvider,
	AiWidgetProviderQuery,
	type aiWidgetProviderQuery,
} from '@atlassian/csm-ai-search-trigger';
import { HubAppShell } from '@atlassian/hub-app-shell';
import { getEmojiProvider } from '@atlassian/hub-providers';
import { HubRenderer } from '@atlassian/hub-renderer';
import { useRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';
import { RovoWidgetConfigProvider } from '@atlassian/rovo-service-agent/controllers/rovo-widget-config-context';

import { useProductExtensionHandlers } from '../../controllers/use-product-extension-handlers';
import {
	hubBootstrapResource,
	hubPageResource,
	rovoWidgetResource,
} from '../../services/resources';
import { useRelayResource } from '../../services/resources/utils';
import { AiCornerAgentRenderer } from '../../ui/ai-corner-agent-renderer';
import { HubsDocumentTitle } from '../../ui/document-title';
import type { hubQuery } from './__generated__/hubQuery.graphql';
import { HubPageBreadcrumbs } from './hub-page-breadcrumbs';
import messages from './messages';

declare global {
	interface Window {
		__SSR_RENDERED__?: boolean;
	}
}

const styles = cssMap({
	spinnerContainer: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		height: '100vh',
	},
});

interface HubInnerProps {
	appearance?: RendererAppearance;
	extensionHandlers: any;
	helpCenterData: any;
	mediaProvider: MediaProvider;
	parsedAdf: DocNode;
	productExtensionsDependenciesProvider: ComponentType<ProductDependenciesProviderProps>;
	providerFactory: ProviderFactory;
}

interface HubInternalProps extends HubAppProps {
	customPageBreadcrumbPath?: string;
	portalBreadcrumbPath?: string;
	showCustomPageBreadcrumbs?: boolean;
	showPortalBreadcrumbs?: boolean;
}

const HubInner = ({
	appearance = 'full-page',
	parsedAdf,
	mediaProvider,
	providerFactory,
	extensionHandlers,
	helpCenterData,
	productExtensionsDependenciesProvider,
}: HubInnerProps) => {
	return (
		<MediaClientProvider clientConfig={mediaProvider.viewMediaClientConfig}>
			{/*
			 * Applies the persisted space density to view-mode content. Scoped to
			 * the dynamic hub pages (home, custom, portal) because only they render
			 * HubInner/HubRenderer — the static editors (request create/details) do
			 * not. No-op unless the space-density gate is on and a density is set.
			 */}
			<HubSpacingScope>
				<HubRenderer
					document={parsedAdf}
					appearance={appearance}
					dataProviders={providerFactory}
					product={helpCenterTypeToProduct(helpCenterData?.type || '')}
					additionalExtensionHandlers={extensionHandlers}
					contentId={helpCenterData?.pages?.[0]?.id || ''}
					PropsProvider={productExtensionsDependenciesProvider}
				/>
			</HubSpacingScope>
		</MediaClientProvider>
	);
};

const Hub = ({
	queryReference,
	appearance = 'full-page',
	showPageNameAsHubDocumentTitle = false,
	productExtensionsDependenciesProvider = CommonHubExtensionsDependenciesProvider,
	customPageBreadcrumbPath,
	portalBreadcrumbPath,
	showCustomPageBreadcrumbs = false,
	showPortalBreadcrumbs = false,
}: {
	appearance?: RendererAppearance;
	customPageBreadcrumbPath?: string;
	portalBreadcrumbPath?: string;
	productExtensionsDependenciesProvider?: ComponentType<ProductDependenciesProviderProps>;
	queryReference: PreloadedQuery<hubQuery>;
	showCustomPageBreadcrumbs?: boolean;
	showPageNameAsHubDocumentTitle?: boolean;
	showPortalBreadcrumbs?: boolean;
}) => {
	// When the fix flag is ON, `HubAppShell` is rendered one level up in
	// `HubApp` so it stays mounted across page navigations. When the flag is
	// OFF, `Hub` renders `HubAppShell` itself (original behaviour).
	di(HubAppShell, useRelayResource);
	const hubContext = useHubContext();
	if (!hubContext) {
		throw new Error('HubContext not found. HubBootstrap must be used as a parent component.');
	}

	const {
		user,
		cloudId,
		env: { helpCenterAri },
	} = hubContext;

	// When the fix flag is ON, `pageMediaConfig` is fetched here (page-scoped)
	// so the bootstrap query stays page-agnostic and is not refetched on every
	// page navigation. When the flag is OFF, `pageMediaConfig` is skipped here
	// and remains on the bootstrap query as before.
	/* eslint-disable @atlassian/relay/unused-fields */
	const data = usePreloadedQuery<hubQuery>(
		graphql`
			query hubQuery(
				$cloudId: ID!
				$hubAri: ID!
				$isDraft: Boolean = false
				$pageAri: ID!
				$operationType: HelpCenterMediaConfigOperationType
				$rovoConfigFromKosh: Boolean = false
				$includeCustomPageParents: Boolean = false
			) {
				helpCenter(cloudId: $cloudId) {
					pageMediaConfig: hubMediaConfig(
						ari: $pageAri
						isDraft: $isDraft
						operationType: $operationType
					) @required(action: THROW) {
						asapIssuer @required(action: THROW)
						mediaCollectionName @required(action: THROW)
						mediaToken @required(action: THROW)
						mediaUrl @required(action: THROW)
					}
					helpCenterById(helpCenterAri: $hubAri) @optIn(to: "HelpCenterAggBeta") {
						__typename
						... on HelpCenter {
							type @optIn(to: "HelpCenterTypeTest")
							name {
								default
							}

							helpCenterBranding @optIn(to: "HelpCenterBrandingTest") {
								navigationBar @optIn(to: "HelpCenterNavigationBarTest") {
									backgroundColor
									borderColor
									borderType
									cornerRadius
									padding
									positionType
									textColor
								}
							}

							pages(filter: { ids: [$pageAri] }) {
								id
								name {
									default
								}
								parents @include(if: $includeCustomPageParents) {
									name
								}
								# Hub-scoped Rovo widget config, served by help-kosh from convo-ai's
								# licence-independent resolver. Identical for every page of a hub
								# (CUSTOM_HOME / CUSTOM / PORTAL) because help-kosh resolves it from
								# the page's helpCenterAri. Null while the server-side
								# convo-ai-resolver-jsm-entitlement gate is off, which the callout
								# banner reads as "no Rovo" — it does not fall back once this gate
								# is on.
								rovoConfig @include(if: $rovoConfigFromKosh) {
									isCustomAgentsAvailable
									isRovoEnabled
									agentId
									widgetContainerAri
								}
								pageLayout {
									layoutAdf {
										content {
											edges {
												node {
													content
												}
											}
										}
									}
								}
							}
						}
						... on QueryError {
							message
						}
					}
				}
			}
		`,
		queryReference,
	);
	/* eslint-enable @atlassian/relay/unused-fields */

	// When the fix flag is ON, read `pageMediaConfig` from the page-scoped
	// hubQuery result. When OFF, it comes from the bootstrap via `hubContext`.
	const pageMediaConfigFromQuery = data.helpCenter?.pageMediaConfig;
	const pageMediaConfigFromContext = hubContext.pageMediaConfig;

	const pageMediaConfig = fg('hubs_fix_rerendering_bootstrap_page_media')
		? pageMediaConfigFromQuery
		: pageMediaConfigFromContext;

	if (!pageMediaConfig) {
		throw new Error('pageMediaConfig not found');
	}
	const { mediaCollectionName, asapIssuer, mediaToken, mediaUrl } = pageMediaConfig;

	const helpCenterData = data.helpCenter?.helpCenterById;

	if (helpCenterData?.__typename === 'QueryError') {
		throw new Error(`QueryError in hub query: ${helpCenterData.message}`);
	}

	if (helpCenterData?.__typename !== 'HelpCenter') {
		throw new Error('HelpCenter not found in hub query');
	}

	const helpCenterType = helpCenterData?.type || '';

	const pageName = helpCenterData.pages?.[0]?.name?.default;
	const pageParents = helpCenterData.pages?.[0]?.parents;
	const parentFolderName = pageParents?.[pageParents.length - 1]?.name;
	const title = showPageNameAsHubDocumentTitle && pageName ? pageName : '';

	const mediaProvider: MediaProvider = getMediaProvider(
		mediaCollectionName,
		asapIssuer,
		mediaToken,
		mediaUrl,
	);
	const emojiProvider: Promise<EmojiProvider> = getEmojiProvider({
		cloudId: queryReference.variables.cloudId,
		// NOTE: id is not yet available in the user object, but will be in the near future
		// as per https://bitbucket.org/atlassian/atlassian-frontend-monorepo/pull-requests/186871/overview#comment-659373408.
		// userId is optional for getEmojiProvider so just leaving this here for now.
		userId: user.id,
	});

	const providerFactory = React.useMemo(() => {
		return ProviderFactory.create({
			mediaProvider: Promise.resolve(mediaProvider),
			emojiProvider: Promise.resolve(emojiProvider),
		});
	}, [mediaProvider, emojiProvider]);

	const layoutAdf =
		helpCenterData.pages?.[0]?.pageLayout?.layoutAdf?.content?.edges
			?.map((edge) => edge?.node?.content)
			?.join('') || '{}';

	const parsedAdf = JSON.parse(layoutAdf) as DocNode;

	const environment = useRelayEnvironmentProvider().getEnvironment({});

	// The Rovo widget query is preloaded as a route resource (in parallel with the
	// hub bootstrap/page queries) so the help-hub callout banner's "Ask Rovo" search
	// box doesn't wait on a late, lazily-fired query. The resource is only attached to
	// the route when the `hub_preload_rovo_widget_query` gate is on (see the route
	// factories); when it isn't attached, the router never loads it and this reads
	// `null`. The reference is forwarded to the (help-hub-only) callout banner handler.
	const { queryReference: rovoWidgetProviderQueryReference } = useRelayResource(rovoWidgetResource);

	// Use product extension handlers hook to get additional handlers for the product
	const extensionHandlers = useProductExtensionHandlers({
		contentId: queryReference.variables.hubAri,
		PropsProvider: productExtensionsDependenciesProvider,
		product: helpCenterType,
		rovoWidgetQueryReference: rovoWidgetProviderQueryReference,
	});

	const aiWidgetProviderQueryReference = useMemo(() => {
		if (helpCenterType !== 'CUSTOMER_SERVICE') {
			return null;
		}

		return loadQuery(environment, AiWidgetProviderQuery, {
			cloudId,
			helpCenterAri,
		}) as PreloadedQuery<aiWidgetProviderQuery>;
	}, [helpCenterType, environment, cloudId, helpCenterAri]);

	const navigationBar = helpCenterData?.helpCenterBranding?.navigationBar ?? null;

	const { branding } = useHubContext();
	const brandColor = branding?.colors?.primary;
	const isCornerRadiusEnabled = useIsHubCornerRadiusEnabled();
	const isAdvancedThemingEnabled = useIsAdvancedThemingEnabled();

	const navStyleOptions = useMemo<NavigationStyleContextType>(
		() => ({
			options: navigationBar
				? mapApiToNavigationOptions(
						navigationBar,
						brandColor,
						isCornerRadiusEnabled,
						isAdvancedThemingEnabled,
					)
				: null,
			setOptions: () => void 0,
		}),
		[navigationBar, brandColor, isCornerRadiusEnabled, isAdvancedThemingEnabled],
	);

	// When the fix flag is ON, re-provide `HubContext` with `pageMediaConfig`
	// from the page-scoped query so descendants can read it via
	// `useHubContext()` without a separate hook. When the flag is OFF the
	// bootstrap already set `pageMediaConfig` on the context, so no
	// re-provision is needed.
	const hubContextWithPageMedia = useMemo(
		() => ({ ...hubContext, pageMediaConfig }),
		[hubContext, pageMediaConfig],
	);

	// Derived from the page-scoped `rovoConfig` (absent unless `hubs_rovo_config_from_kosh` is on).
	// See `deriveRovoWidgetConfig` for exactly when this is null.
	const koshRovoWidgetConfig = useMemo(
		() => deriveRovoWidgetConfig(helpCenterData.pages?.[0]?.rovoConfig),
		[helpCenterData],
	);

	const inner = (
		<NavigationStyleContext.Provider value={navStyleOptions}>
			<HubsDocumentTitle title={title} shouldRenderDocumentTitle={showPageNameAsHubDocumentTitle}>
				<React.Fragment>
					{helpCenterType === 'HELP_HUB' && showCustomPageBreadcrumbs && (
						<HubPageBreadcrumbs
							pageName={pageName}
							pagePath={customPageBreadcrumbPath}
							pageType="custom"
							parentFolderName={parentFolderName}
						/>
					)}
					{helpCenterType === 'HELP_HUB' && showPortalBreadcrumbs && portalBreadcrumbPath && (
						<HubPageBreadcrumbs
							pageName={pageName}
							pagePath={portalBreadcrumbPath}
							pageType="portal"
						/>
					)}
					<HubInner
						appearance={appearance}
						parsedAdf={parsedAdf}
						mediaProvider={mediaProvider}
						providerFactory={providerFactory}
						extensionHandlers={extensionHandlers}
						helpCenterData={helpCenterData}
						productExtensionsDependenciesProvider={productExtensionsDependenciesProvider}
					/>
					<Suspense fallback={<UFOLoadHold name="ai-corner-agent-renderer-loader" />}>
						{aiWidgetProviderQueryReference && (
							<ErrorBoundary fallback={null}>
								<AiWidgetProvider queryReference={aiWidgetProviderQueryReference}>
									<AiCornerAgentRenderer />
								</AiWidgetProvider>
							</ErrorBoundary>
						)}
					</Suspense>
				</React.Fragment>
			</HubsDocumentTitle>
		</NavigationStyleContext.Provider>
	);

	// When the fix flag is ON, wrap with a HubContext.Provider that merges
	// in the page-scoped `pageMediaConfig`. When OFF, restore the original
	// behaviour: render `HubAppShell` here (wrapping `inner`) and use the
	// bootstrap context which already has `pageMediaConfig`.
	const content = fg('hubs_fix_rerendering_bootstrap_page_media') ? (
		<HubContext.Provider value={hubContextWithPageMedia}>{inner}</HubContext.Provider>
	) : (
		<HubAppShell>{inner}</HubAppShell>
	);

	// Provide the help-kosh-sourced config here so the callout banner reads it via
	// `useOptionalRovoWidgetConfig()` with no second AGG round trip. The banner skips its own
	// query-backed provider when this one is already in the tree, so this path takes precedence
	// and the query-backed one remains the fallback.
	return koshRovoWidgetConfig ? (
		<RovoWidgetConfigProvider rovoWidgetConfig={koshRovoWidgetConfig}>
			{content}
		</RovoWidgetConfigProvider>
	) : (
		content
	);
};

export const HubApp = (props: HubInternalProps): JSX.Element | null => {
	di(useRelayResource);
	di(HubBootstrapWrapper, HubAppShell);

	const { appearance = 'full-page' } = props;
	const { queryReference: hubPageQueryReference } = useRelayResource(hubPageResource);

	const { queryReference: hubBootstrapQueryReference } = useRelayResource(hubBootstrapResource);

	const { formatMessage } = useIntl();

	if (!hubPageQueryReference || !hubBootstrapQueryReference) {
		return null;
	}

	const cloudId = hubBootstrapQueryReference.variables.cloudId;

	return (
		<HubErrorBoundary
			packageName="hub"
			id="hub-page"
			fallback={<GenericErrorPage />}
			shouldForceError={true}
			operationNames={['hubBootstrapWrapperQuery']}
		>
			<ContextualAnalyticsData sourceType={SCREEN} sourceName="hub">
				<Suspense
					fallback={
						<UFOLoadHold name="hub-loader">
							<div css={styles.spinnerContainer}>
								<Spinner size="large" label={formatMessage(messages.hubLoadingSpinnerLabel)} />
							</div>
						</UFOLoadHold>
					}
				>
					<HubBootstrapWrapper
						queryReference={hubBootstrapQueryReference}
						cloudId={cloudId}
						{...props}
					>
						{fg('hubs_fix_rerendering_bootstrap_page_media') ? (
							// Flag ON: HubAppShell lives outside Hub so the app
							// shell is not remounted on every page navigation.
							<HubAppShell>
								<Suspense
									fallback={
										<UFOLoadHold name="hub-loader">
											<div css={styles.spinnerContainer}>
												<Spinner
													size="large"
													label={formatMessage(messages.hubLoadingSpinnerLabel)}
												/>
											</div>
										</UFOLoadHold>
									}
								>
									<Hub
										appearance={appearance}
										customPageBreadcrumbPath={props.customPageBreadcrumbPath}
										portalBreadcrumbPath={props.portalBreadcrumbPath}
										queryReference={hubPageQueryReference}
										showPageNameAsHubDocumentTitle={props?.showPageNameAsHubDocumentTitle}
										showCustomPageBreadcrumbs={props.showCustomPageBreadcrumbs}
										showPortalBreadcrumbs={props.showPortalBreadcrumbs}
										productExtensionsDependenciesProvider={
											props.productExtensionsDependenciesProvider
										}
									/>
								</Suspense>
							</HubAppShell>
						) : (
							// Flag OFF: original behaviour — Hub renders HubAppShell
							// internally on every page render.
							<Hub
								appearance={appearance}
								customPageBreadcrumbPath={props.customPageBreadcrumbPath}
								portalBreadcrumbPath={props.portalBreadcrumbPath}
								queryReference={hubPageQueryReference}
								showPageNameAsHubDocumentTitle={props?.showPageNameAsHubDocumentTitle}
								showCustomPageBreadcrumbs={props.showCustomPageBreadcrumbs}
								showPortalBreadcrumbs={props.showPortalBreadcrumbs}
								productExtensionsDependenciesProvider={props.productExtensionsDependenciesProvider}
							/>
						)}
					</HubBootstrapWrapper>
				</Suspense>
				<FireScreenAnalytics />
			</ContextualAnalyticsData>
		</HubErrorBoundary>
	);
};
